import { Footer } from "@/components/sections/Footer";
import { HeroSection } from "@/components/services/managed/HeroSection";
import { ChallengesSection } from "@/components/services/managed/ChallengesSection";
import { ApproachSection } from "@/components/services/managed/ApproachSection";
import { ProcessSection } from "@/components/services/managed/ProcessSection";
import { ValueMetricsSection } from "@/components/services/managed/ValueMetricsSection";
import { SuccessStoriesSection } from "@/components/services/managed/SuccessStoriesSection";
import { FaqSection } from "@/components/services/managed/FaqSection";
import { ComparisonSection } from "@/components/services/managed/ComparisonSection";
import { CalculatorSection } from "@/components/services/managed/CalculatorSection";
import { CtaSection } from "@/components/services/managed/CtaSection";
import { useEffect } from "react";
import { PageLayout } from "@/components/layout/PageLayout";

const ManagedServices = () => {
  const pageTitle = "Managed IT Services | Reliable Technology Support | edgefi";
  const pageDescription = "Focus on your business, not your technology problems. Our managed IT services provide reliable, proactive support that eliminates frustration and helps your business thrive.";
  const pageUrl = window.location.origin + "/services/managed-services";

  // Add JSON-LD structured data for SEO
  useEffect(() => {
    // Service schema
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Managed IT Services",
      "provider": {
        "@type": "Organization",
        "name": "edgefi",
        "url": window.location.origin
      },
      "serviceType": "IT Management",
      "description": "Comprehensive IT management services to optimize performance, reduce costs, and ensure business continuity.",
      "offers": {
        "@type": "Offer",
        "description": "Proactive monitoring, network management, cloud services, and end-user support"
      },
      "areaServed": {
        "@type": "Country",
        "name": "United States"
      },
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "SMB, Enterprise"
      },
      "url": pageUrl
    });
    document.head.appendChild(serviceScript);
    
    // BreadcrumbList schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": window.location.origin
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": `${window.location.origin}/services`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Managed IT Services",
          "item": pageUrl
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);
    
    // FAQ schema (for the FAQ section)
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is included in your managed IT services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our managed IT services include 24/7 monitoring, proactive maintenance, help desk support, cloud services management, network management, strategic IT planning, and regular technology reviews."
          }
        },
        {
          "@type": "Question",
          "name": "How quickly can you respond to IT issues?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our service level agreements guarantee response times based on issue severity, with critical issues receiving immediate attention within 15-30 minutes, and regular support requests addressed within 1-4 hours."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer cloud migration services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we provide comprehensive cloud migration services, including readiness assessment, migration planning, implementation, and ongoing management for Microsoft 365, Azure, AWS, and other cloud platforms."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      document.head.removeChild(serviceScript);
      document.head.removeChild(breadcrumbScript);
      document.head.removeChild(faqScript);
    };
  }, [pageUrl]);

  return (
    <PageLayout
      title={pageTitle}
      description={pageDescription}
      canonicalUrl={pageUrl}
      keywords={["managed IT services", "IT support", "reliable technology", "network services", "help desk", "business technology", "proactive maintenance", "IT frustration"]}
      schema={{
        type: "service",
        data: {
          name: "Managed IT Services",
          description: "Comprehensive IT management services to optimize performance, reduce costs, and ensure business continuity.",
          provider: {
            name: "edgefi",
            url: window.location.origin
          }
        }
      }}
    >
      <HeroSection />
      <ChallengesSection />
      <ApproachSection />
      <ValueMetricsSection />
      <ComparisonSection />
      <ProcessSection />
      <CalculatorSection />
      <SuccessStoriesSection />
      <FaqSection />
      <CtaSection />
    </PageLayout>
  );
};

export default ManagedServices;

